/**
 * Pure steps behind the checklist's optimistic writes: what a tick or fill
 * does to the record before the server answers, and how to take back exactly
 * that change when the server refuses it.
 *
 * @module state/findingFixReview.logic
 */
import { FIX_FILL_MAX_LENGTH, type FixReviewView, type FixReviewWrite } from "@repo/shared/findingChanges";

/** A fill value as the server stores it: trimmed and bounded. */
export const normalizeFillValue = (value: string): string => value.trim().slice(0, FIX_FILL_MAX_LENGTH);

/**
 * `record[key]` only when the record holds it itself: a fill key such as
 * `constructor` must never read the prototype's function.
 */
const ownEntry = <T,>(record: Readonly<Record<string, T>>, key: string): T | undefined =>
  Object.prototype.hasOwnProperty.call(record, key) ? record[key] : undefined;

/**
 * The record after `write`, or the same object when the write changes
 * nothing (a repeated tick, an unchanged fill), so callers can skip it.
 */
export function applyFixReviewWrite(
  review: FixReviewView,
  write: FixReviewWrite,
  actor: { readonly name: string | null; readonly at: string },
): FixReviewView {
  if (write.action === "confirm") {
    const key = String(write.index);
    if (Boolean(ownEntry(review.confirmations, key)) === write.confirmed) return review;
    const { [key]: _previous, ...rest } = review.confirmations;
    return {
      ...review,
      confirmations: write.confirmed ? { ...rest, [key]: { byId: null, by: actor.name, at: actor.at } } : rest,
    };
  }
  const value = normalizeFillValue(write.value);
  if ((ownEntry(review.fills, write.key) ?? "") === value) return review;
  const { [write.key]: _previous, ...rest } = review.fills;
  return { ...review, fills: value ? { ...rest, [write.key]: value } : rest };
}

/** `current` with the entry `write` touched put back to what it was in `before`. */
export function revertFixReviewWrite(current: FixReviewView, before: FixReviewView, write: FixReviewWrite): FixReviewView {
  if (write.action === "confirm") {
    const key = String(write.index);
    // A later write to the same entry has replaced this one: keep it.
    if (Boolean(ownEntry(current.confirmations, key)) !== write.confirmed) return current;
    const { [key]: _current, ...rest } = current.confirmations;
    const previous = ownEntry(before.confirmations, key);
    return { ...current, confirmations: previous ? { ...rest, [key]: previous } : rest };
  }
  if ((ownEntry(current.fills, write.key) ?? "") !== normalizeFillValue(write.value)) return current;
  const { [write.key]: _current, ...rest } = current.fills;
  const previous = ownEntry(before.fills, write.key);
  return { ...current, fills: previous !== undefined ? { ...rest, [write.key]: previous } : rest };
}

/**
 * Writes still on their way, counted per proposal. The page can move to
 * another proposal while a write is pending; that write must not hold back
 * the new proposal's fresh read.
 */
export function createInFlightWrites() {
  const counts = new Map<string, number>();
  return {
    begin(key: string): void {
      counts.set(key, (counts.get(key) ?? 0) + 1);
    },
    end(key: string): void {
      const left = (counts.get(key) ?? 0) - 1;
      if (left > 0) counts.set(key, left);
      else counts.delete(key);
    },
    idle(key: string): boolean {
      return !counts.has(key);
    },
  };
}
